import { useParams } from "react-router";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq.ts";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { Loading } from "src/components/utils/Loading.tsx";
import { URLHandler } from "src/services/URLHandler";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { ReactNode, useEffect } from "react";
import { useGetOrganizationScores } from "src/api/client-api/olapScores/useGetOrganizationScores";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import { OlapParamsService } from "src/context/OlapScores/OlapParamsService";
import { Dimensions } from "src/context/OlapScores/useDimensions";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export const getCompanyScoresHeadingsApiKey = "getCompanyScoresHeadingsApiKey" as const;

type CompanyScoreContextProviderProps = {
    children: ReactNode | ReactNode[];
};

const CompanyScoreContextProvider = (props: CompanyScoreContextProviderProps) => {
    const { children } = props;

    const {
        scoreId: scoreIdString,
        ssqId: ssqIdString,
        campaignId: campaignIdString,
    } = useParams<{
        scoreId: string;
        ssqId: string;
        campaignId: string;
    }>();

    const scoreId = Number(scoreIdString);
    const ssqId = Number(ssqIdString);
    const campaignId = Number(campaignIdString);

    const scoreDetailDimensions: Dimensions = [
        [["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
        [["ssq_versions"], ["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
    ];
    const initialDimensions = OlapParamsService.getInitialDimensions(scoreDetailDimensions);

    // Utiliser les données du EntityScoresProvider (comme ProductScoreContextProvider)
    const { scoresWithVariations, selectedVariations, getEntityScore, setEntitySsq } = useEntityScores();
    const { company } = useCompanyMetadata();

    // Récupérer le QSS spécifique de l'URL au lieu du QSS principal
    const { ssq, isReady: ssqReady } = useGetOneSsq(ssqId);
    const score = getEntityScore(scoreId);

    const scoresParams = useOlapScoresParams({
        scores: {
            ids: [scoreId],
            dimensions: initialDimensions,
            variations: selectedVariations.query,
        },
        prevScores: true,
    });

    const areAggregationsRequested = scoresParams?.query.scores?.aggregations !== undefined;

    const {
        data: scores,
        isLoading,
        isRefetching,
    } = useGetOrganizationScores({ companyId: company.id, ssqId, campaignId, scoresQuery: scoresParams.query });

    const {
        data: post,
        isLoading: isPostLoading,
        refetch: fetchPostForAnswers,
    } = useGetOrganizationPost({
        companyId: company.id,
        ssqId,
        campaignId,
    });

    const { data: ssqVersion, isLoading: isSsqVersionLoading } = useGetOneSsqVersion(post?.ssqVersionId);

    useEffect(() => {
        setEntitySsq(ssq);
    }, [ssq]);

    if (!ssqReady || isPostLoading || isSsqVersionLoading) {
        return <Loading />;
    }

    if (!score || !ssq) {
        return null;
    }

    const scoreWithVariations = scoresWithVariations?.filter(
        (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
    );

    return (
        <ScoreDetailContextProvider
            score={score}
            olapScores={scores?.olapScores[scoreId]}
            olapPrevScores={scores?.olapPrevScores?.[scoreId]}
            scoresParams={scoresParams}
            scoreDetailDimensions={scoreDetailDimensions}
            ignoredDrillDownDimensions={["multivalues"]}
            areAggregationsRequested={areAggregationsRequested}
            olapLocalizedLabelContext={{ ssqVersion }}
            fetchPostForAnswers={fetchPostForAnswers}
            selectedVariations={selectedVariations}
            scoreWithVariations={scoreWithVariations}
            ssqVersion={ssqVersion}
            isLoading={isLoading || isRefetching || !ssqReady}
            prevCampaignLabel={scores?.prevCampaignName}
            goBackLink={URLHandler.company.overview(company.id)}
            tabConfig={{
                distribution: {
                    link: (scoreId) => {
                        return URLHandler.company.ssqScoreDistribution(
                            company.id,
                            ssqId,
                            campaignId,
                            scoreId.toString(),
                        );
                    },
                },
                comparison: {
                    link: (scoreId) => {
                        return URLHandler.company.ssqScoreComparison(company.id, ssqId, campaignId, scoreId.toString());
                    },
                },
                evolution: {
                    link: (scoreId) => {
                        return URLHandler.company.ssqScoreEvolution(company.id, ssqId, campaignId, scoreId.toString());
                    },
                },
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};

export default CompanyScoreContextProvider;
